/**
 * Library Context for PotFoundry Web
 * 
 * Connects to Supabase to fetch and publish designs to the public library.
 * 
 * @module context/LibraryContext
 */

import React, { createContext, useContext, useCallback, useState, useMemo } from 'react';
import { useControllerMaybe } from './ControllerContext';
import { buildStyleParamPayload } from '../utils/styleParams';
import { supabase, isSupabaseConfigured } from '../services/supabase';

// ============================================================================
// Caching Configuration - Reduces Supabase egress by caching API responses
// ============================================================================

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache TTL
const CACHE_KEY_PREFIX = 'pf_lib_cache_';

interface CacheEntry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic cache stores arbitrary JSON-serializable data
  data: any;
  timestamp: number;
}

/**
 * Generate cache key based on query parameters
 */
function getCacheKey(page: number, search: string, style: string | null, myDesigns: boolean): string {
  return `${CACHE_KEY_PREFIX}${page}_${search}_${style ?? 'all'}_${myDesigns}`;
}

/**
 * Get cached data from sessionStorage if still valid
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic cache returns arbitrary JSON data
function getCachedData(key: string): any | null {
  try {
    const cached = sessionStorage.getItem(key);
    if (!cached) return null;

    const entry: CacheEntry = JSON.parse(cached);
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

/**
 * Store data in sessionStorage cache
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic cache stores arbitrary JSON-serializable data
function setCachedData(key: string, data: any): void {
  try {
    const entry: CacheEntry = { data, timestamp: Date.now() };
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

/**
 * Clear all library cache entries
 */
function clearLibraryCache(): void {
  try {
    const keys = Object.keys(sessionStorage).filter(k => k.startsWith(CACHE_KEY_PREFIX));
    keys.forEach(k => sessionStorage.removeItem(k));
  } catch {
    // Ignore errors
  }
}

// ============================================================================
// Types
// ============================================================================

export interface LibraryDesign {
  id: string;  // UUID (auto-generated by Supabase)
  user_id?: string;  // Owner of the design
  title: string;
  style: string;
  created_at: string;
  thumb_url?: string;
  stl_url?: string;
  license?: string;
  tags?: string[];
  size?: {
    height?: number;
    top_od?: number;
    bottom_od?: number;
    wall_thickness?: number;
    bottom_thickness?: number;
    drain_radius?: number;
    flare_exp?: number;
  };
  opts?: Record<string, unknown>;
  // Appearance/color settings
  appearance?: {
    primaryColor?: string;    // Bottom of pot gradient (hex)
    midColor?: string;        // Middle of pot gradient (hex)
    secondaryColor?: string;  // Top of pot gradient (hex)
    gradient?: [string, string]; // Background gradient colors
    gradientAngle?: number;   // Background gradient angle in degrees
    lightingPreset?: string;  // Lighting preset name
  };
  mesh?: {
    n_theta?: number;
    n_z?: number;
    twist?: number;
  };
  diagnostics?: {
    vertex_count?: number;
    triangle_count?: number;
  };
  app_commit?: string;
}

export interface LibraryState {
  designs: LibraryDesign[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  page: number;
  searchQuery: string;
  styleFilter: string | null;
  publishing: boolean;
  publishSuccess: boolean | null;
  publishError: string | null;
  filterMyDesigns: boolean;
  ready: boolean;
}

export interface LibraryActions {
  fetchDesigns: (reset?: boolean, pageOverride?: number, forceRefresh?: boolean) => void;
  loadDesign: (design: LibraryDesign) => Promise<void>;
  downloadSTL: (design: LibraryDesign) => void;
  publish: (title: string, tags: string[], license: string) => void;
  clearPublishStatus: () => void;
  setSearchQuery: (query: string) => void;
  setStyleFilter: (style: string | null) => void;
  setFilterMyDesigns: (filter: boolean) => void;
  loadMore: () => void;
}

export interface LibraryContextValue {
  state: LibraryState;
  actions: LibraryActions;
}

export interface LibraryProviderProps {
  children: React.ReactNode;
  libraryData?: unknown | null;
}

const PAGE_SIZE = 20;

// ============================================================================
// Context
// ============================================================================

const LibraryContext = createContext<LibraryContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export const LibraryProvider: React.FC<LibraryProviderProps> = ({ children }) => {
  const controller = useControllerMaybe();

  const [state, setState] = useState<LibraryState>({
    designs: [],
    loading: false,
    error: null,
    hasMore: false,
    page: 1,
    searchQuery: '',
    styleFilter: null,
    publishing: false,
    publishSuccess: null,
    publishError: null,
    filterMyDesigns: false,
    ready: isSupabaseConfigured(),
  });

  // Fetch designs from Supabase with caching to minimize egress
  const fetchDesigns = useCallback(async (reset = false, pageOverride?: number, forceRefresh = false) => {
    if (!supabase) {
      console.warn('[LibraryContext] Supabase not configured');
      return;
    }

    const currentPage = reset ? 1 : (pageOverride ?? state.page);
    const cacheKey = getCacheKey(currentPage, state.searchQuery, state.styleFilter, state.filterMyDesigns);

    // Check cache first (unless forcing refresh)
    if (!forceRefresh && !reset) {
      const cached = getCachedData(cacheKey);
      if (cached) {
        console.debug('[LibraryContext] Cache hit, skipping API call');
        setState(s => ({
          ...s,
          loading: false,
          designs: currentPage === 1 ? cached.designs : [...s.designs, ...cached.designs],
          hasMore: cached.hasMore,
          page: currentPage,
        }));
        return;
      }
    }

    setState(s => ({
      ...s,
      loading: true,
      error: null,
      page: reset ? 1 : s.page,
      designs: reset ? [] : s.designs
    }));

    try {
      const from = (currentPage - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      // Fetch columns needed for list view including size/opts for thumbnail rendering
      let query = supabase
        .from('designs')
        .select('id, title, style, created_at, license, tags, user_id, size, opts')
        .order('created_at', { ascending: false })
        .range(from, to);

      // Apply filters
      if (state.searchQuery) {
        query = query.ilike('title', `%${state.searchQuery}%`);
      }
      if (state.styleFilter) {
        query = query.eq('style', state.styleFilter);
      }
      // Filter to user's own designs if enabled
      if (state.filterMyDesigns) {
        const session = await supabase.auth.getSession();
        const userId = session.data.session?.user?.id;
        if (userId) {
          query = query.eq('user_id', userId);
        }
      }

      const { data, error } = await query;

      if (error) {
        console.error('[LibraryContext] Fetch error:', error);
        setState(s => ({ ...s, loading: false, error: error.message }));
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase REST row is untyped
      const designs: LibraryDesign[] = (data || []).map((d: any) => ({
        id: d.id,
        user_id: d.user_id,
        title: d.title,
        style: d.style,
        created_at: d.created_at,
        thumb_url: d.thumb_url,
        stl_url: d.stl_url,
        license: d.license,
        tags: d.tags || [],
        size: d.size || {},
        opts: d.opts || {},
        mesh: d.mesh || {},
        diagnostics: d.diagnostics || {},
        app_commit: d.app_commit,
      }));

      const hasMore = designs.length === PAGE_SIZE;

      // Cache the response
      setCachedData(cacheKey, { designs, hasMore });

      setState(s => ({
        ...s,
        loading: false,
        designs: reset ? designs : [...s.designs, ...designs],
        hasMore,
        page: currentPage,
      }));
    } catch (err) {
      console.error('[LibraryContext] Fetch error:', err);
      setState(s => ({ ...s, loading: false, error: String(err) }));
    }
  }, [state.page, state.searchQuery, state.styleFilter, state.filterMyDesigns]);

  const loadDesign = useCallback(async (design: LibraryDesign) => {
    if (!supabase) {
      console.warn('[LibraryContext] Supabase not configured');
      return;
    }

    // Fetch full design data on-demand (list view only has minimal fields)
    const { data: fullDesign, error } = await supabase
      .from('designs')
      .select('*')
      .eq('id', design.id)
      .single();

    if (error || !fullDesign) {
      console.error('[LibraryContext] Failed to load design:', error);
      return;
    }

    // Build WebGPU params directly from the design data
    const size = fullDesign.size || {};
    const opts = fullDesign.opts || {};

    const H = size.height ?? 120;
    const top_od = size.top_od ?? 140;
    const bottom_od = size.bottom_od ?? 90;
    const Rt = top_od * 0.5;
    const Rb = bottom_od * 0.5;
    const t_wall = size.wall_thickness ?? 3;
    const t_bottom = size.bottom_thickness ?? 3;
    const r_drain = size.drain_radius ?? 10;
    const expn = size.flare_exp ?? 1.1;

    // Extract bell parameters from opts (Python uses snake_case)
    const bellAmp = (opts.bell_amp as number) ?? 0.0;
    const bellCenter = (opts.bell_center as number) ?? 0.5;
    const bellWidth = (opts.bell_width as number) ?? 0.22;

    // Extract spin/twist parameters from opts (stored in snake_case, degrees for phase)
    const spinTurns = (opts.spin_turns as number) ?? 0;
    const spinPhaseDeg = (opts.spin_phase as number) ?? 0; // Stored in DEGREES (like UI)
    const spinCurve = (opts.spin_curve as number) ?? 1;

    const [styleId, styleParams] = buildStyleParamPayload(fullDesign.style, opts);

    const baseRadius = Math.max(Rt, Rb, 1);
    const halfHeight = Math.max(H * 0.5, 1);
    const sceneRadius = Math.sqrt(baseRadius * baseRadius + halfHeight * halfHeight);

    const wgpuParams: Record<string, unknown> = {
      H, Rt, Rb, expn, t_wall, t_bottom, r_drain, drain: r_drain,
      bellAmp, bellCenter, bellWidth,
      spin_turns: spinTurns,
      spin_phase: spinPhaseDeg * Math.PI / 180, // Convert degrees to radians for WebGPU
      spin_curve: spinCurve,
      styleId, styleParams,
      sceneRadius: sceneRadius * 1.2,
      scenePadding: 1.2,
      paramUpdate: true,
      paramUpdateNonce: Date.now(),
    };

    if (controller?.updateParams) {
      controller.updateParams(wgpuParams);

      // Sync store with loaded design parameters
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Debug global for store access
      const store = (window as any).__POTFOUNDRY_STORE__;
      if (store) {
        const state = store.getState();

        // Sync geometry (including spin/twist params for UI sliders)
        state.setGeometryParams({
          H,
          top_od,
          bottom_od,
          t_wall,
          t_bottom,
          r_drain,
          expn,
          bellAmp,
          bellCenter,
          bellWidth,
          // Spin/twist params - pass degrees directly (UI stores degrees)
          spinTurns,
          spinPhase: spinPhaseDeg,
          spinCurve,
        });

        // Sync style
        if (fullDesign.style) {
          state.setStyle(fullDesign.style);
          if (opts && typeof opts === 'object') {
            state.setStyleOpts(opts as Record<string, number | boolean>);
          }
        }

        // Sync appearance (colors) if available
        const appearance = fullDesign.appearance;
        if (appearance) {
          if (appearance.primaryColor) state.setPrimaryColor(appearance.primaryColor);
          if (appearance.midColor) state.setMidColor(appearance.midColor);
          if (appearance.secondaryColor) state.setSecondaryColor(appearance.secondaryColor);
          if (appearance.gradient) state.setBackgroundGradient(appearance.gradient);
          if (appearance.gradientAngle !== undefined) state.setGradientAngle(appearance.gradientAngle);
          if (appearance.lightingPreset) state.setLightingPreset(appearance.lightingPreset);
        }
      }
    }
  }, [controller]);

  const downloadSTL = useCallback((design: LibraryDesign) => {
    if (design.stl_url) {
      window.open(design.stl_url, '_blank');
    }
  }, []);

  const publish = useCallback(async (title: string, tags: string[], license: string) => {
    if (!supabase) {
      console.warn('[LibraryContext] Supabase not configured');
      return;
    }

    setState(s => ({ ...s, publishing: true, publishSuccess: null, publishError: null }));

    try {
      // Get current user session for user_id
      const session = await supabase.auth.getSession();
      const userId = session.data.session?.user?.id;

      if (!userId) {
        const errorMsg = 'Please sign in to publish designs';
        console.error('[LibraryContext] Publish error:', errorMsg);
        setState(s => ({ ...s, publishing: false, publishSuccess: false, publishError: errorMsg }));
        return;
      }

      // Get current pot state from store
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Debug global for store access
      const store = (window as any).__POTFOUNDRY_STORE__;
      if (!store) {
        throw new Error('Store not available');
      }

      const storeState = store.getState();

      const designData = {
        title,
        style: storeState.style.name,
        tags,
        license,
        user_id: userId,
        size: {
          height: storeState.geometry.H,
          top_od: storeState.geometry.top_od,
          bottom_od: storeState.geometry.bottom_od,
          wall_thickness: storeState.geometry.t_wall,
          bottom_thickness: storeState.geometry.t_bottom,
          drain_radius: storeState.geometry.r_drain,
          flare_exp: storeState.geometry.expn,
        },
        // Include both style-specific opts AND spin/bell from geometry
        opts: {
          ...storeState.style.opts,
          // Spin parameters from geometry
          spin_turns: storeState.geometry.spinTurns,
          spin_phase: storeState.geometry.spinPhase,
          spin_curve: storeState.geometry.spinCurve,
          // Bell parameters from geometry
          bell_amp: storeState.geometry.bellAmp,
          bell_center: storeState.geometry.bellCenter,
          bell_width: storeState.geometry.bellWidth,
        },
        // Store appearance/color settings
        appearance: {
          primaryColor: storeState.appearance.primaryColor,
          midColor: storeState.appearance.midColor,
          secondaryColor: storeState.appearance.secondaryColor,
          gradient: storeState.appearance.gradient,
          gradientAngle: storeState.appearance.gradientAngle,
          lightingPreset: storeState.appearance.lightingPreset,
        },
      };

      const { error } = await supabase
        .from('designs')
        .insert([designData]);

      if (error) {
        console.error('[LibraryContext] Publish error:', error);
        setState(s => ({ ...s, publishing: false, publishSuccess: false, publishError: error.message }));
        return;
      }

      setState(s => ({ ...s, publishing: false, publishSuccess: true, publishError: null }));

      // Clear cache and refresh the list
      clearLibraryCache();
      fetchDesigns(true, undefined, true);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error('[LibraryContext] Publish error:', err);
      setState(s => ({ ...s, publishing: false, publishSuccess: false, publishError: errorMsg }));
    }
  }, [fetchDesigns]);

  const setSearchQuery = useCallback((query: string) => {
    setState(s => ({ ...s, searchQuery: query }));
  }, []);

  const setStyleFilter = useCallback((style: string | null) => {
    setState(s => ({ ...s, styleFilter: style }));
  }, []);

  // Clear publish status after toast is shown (prevents duplicate toasts)
  const clearPublishStatus = useCallback(() => {
    setState(s => ({ ...s, publishSuccess: null, publishError: null }));
  }, []);

  const setFilterMyDesigns = useCallback((filter: boolean) => {
    setState(s => ({ ...s, filterMyDesigns: filter }));
  }, []);

  const loadMore = useCallback(() => {
    if (!state.loading && state.hasMore) {
      const nextPage = state.page + 1;
      setState(s => ({ ...s, page: nextPage }));
      fetchDesigns(false, nextPage);
    }
  }, [state.loading, state.hasMore, state.page, fetchDesigns]);

  const actions = useMemo<LibraryActions>(() => ({
    fetchDesigns, loadDesign, downloadSTL, publish, clearPublishStatus,
    setSearchQuery, setStyleFilter, setFilterMyDesigns, loadMore,
  }), [fetchDesigns, loadDesign, downloadSTL, publish, clearPublishStatus, setSearchQuery, setStyleFilter, setFilterMyDesigns, loadMore]);

  const value = useMemo<LibraryContextValue>(() => ({
    state,
    actions,
  }), [state, actions]);

  return (
    <LibraryContext.Provider value={value}>
      {children}
    </LibraryContext.Provider>
  );
};

// ============================================================================
// Hooks
// ============================================================================

export const useLibrary = (): LibraryContextValue => {
  const context = useContext(LibraryContext);
  if (!context) {
    throw new Error('useLibrary must be used within a LibraryProvider');
  }
  return context;
};

export const useLibraryMaybe = (): LibraryContextValue | null => {
  return useContext(LibraryContext);
};
